/**
 * Второй этаж шапки наполняется через портал, а не через контекст с ReactNode.
 *
 * Причина — телеметрия: ConsoleNavMenu получает по SignalR несколько сообщений
 * в секунду. Если гнать разметку вверх по дереву, на каждом сообщении
 * перерисовывалась бы вся шапка вместе с навигацией. Через портал компонент
 * остаётся смонтированным там, где живёт его состояние, а в шапке меняется
 * только содержимое слота.
 */

import { ReactNode, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';

/** Узел второго этажа; null, пока шапка не смонтирована. */
const PageBarSlotContext = createContext<HTMLElement | null>(null);

export const PageBarSlotProvider = PageBarSlotContext.Provider;

export function usePageBarSlot(): HTMLElement | null {
    return useContext(PageBarSlotContext);
}

interface PageBarProps {
    children?: ReactNode;
}

/**
 * Рисует детей во втором этаже шапки. На первом рендере слота ещё нет
 * (ref-колбэк отрабатывает в коммите), поэтому возвращаем null — следующий
 * рендер уже попадает в слот.
 */
export function PageBar({ children }: PageBarProps): ReactNode {
    const slot = usePageBarSlot();

    if (!slot) {
        return null;
    }

    return createPortal(children, slot);
}

interface PageTitleBarProps {
    title: string;
    /** Кнопки раздела справа: «Добавить», «Обновить» и подобное. */
    actions?: ReactNode;
}

/** Типовое наполнение второго этажа для обычных экранов. */
export function PageTitleBar({ title, actions }: PageTitleBarProps): ReactNode {
    return (
        <PageBar>
            <div className='page-title-bar'>
                <h1 className='page-title-bar-title'>{title}</h1>
                {actions && <div className='page-title-bar-actions'>{actions}</div>}
            </div>
        </PageBar>
    );
}

export default PageBar;
